import { useContext, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@iconify/react";
import Markdown from "react-markdown";
import toast from "react-hot-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PinGuard } from "@/components/PinGuard";
import { PinSetupWizard } from "@/components/PinSetupWizard";
import { QrDevice } from "@/components/QrDevice";
import { MainProcessLog } from "@/components/MainProcessLog";
import {
  getScreensaverSettings,
  requestScreensaverPreview,
  saveScreensaverSettings,
} from "@/lib/screensaver";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Get, Put } from "../api/api";
import {
  fetchLatestRelease,
  formatSize,
  hardReload,
  installedVersion,
  isDesktopApp,
  isNewerVersion,
} from "../lib/desktop";
import {
  DEVICE_SHOP_ID_KEY,
  DEVICE_TOKEN_KEY,
  heartbeatDevice,
  notifyDeviceChanged,
} from "../api/device";
import { getComandaOptions, getTicketOptions, printTicket } from "../api/printer";
import { GenerateTicketExample } from "../api/ticket";
import { UserContext } from "../context/user";
import { useAlertDialog } from "@/hooks/useAlertDialog";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { clearPin, getPinConfig } from "@/lib/pin";
import { hasFeature } from "@/lib/features";

const settingsSections = [
  {
    key: "general",
    label: "General",
    description: "Ejercicio, dispositivo y aplicación",
    icon: "mdi:tune-variant",
  },
  {
    key: "printers",
    label: "Impresoras",
    description: "Tickets, papel y comportamiento",
    icon: "mdi:printer-outline",
  },
  {
    key: "comandas",
    label: "Comandas",
    description: "Dispositivo e impresión",
    icon: "mdi:chef-hat",
  },
  {
    key: "screensaver",
    label: "Salvapantallas",
    description: "Bloqueo por inactividad",
    icon: "mdi:monitor-sleep",
  },
  {
    key: "security",
    label: "Seguridad",
    description: "PIN de acceso al TPV",
    icon: "mdi:shield-lock-outline",
  },
];

export function Settings() {
  const [activeSection, setActiveSection] = useState("general");
  const confirm = useConfirmDialog();

  function requestLogout() {
    confirm({
      title: "¿Cerrar sesión?",
      description: "Este TPV volverá a la pantalla de inicio de sesión.",
      okText: "Cerrar sesión",
      cancelText: "Cancelar",
      variant: "danger",
      onOk: () => {
        localStorage.removeItem("token");
        window.location.reload();
      },
    });
  }

  return (
    <PinGuard
      title="Ajustes protegidos"
      description="Introduce el PIN de acceso para abrir la configuración del TPV."
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-neutral-100 select-none">
        <div className="mx-auto flex min-h-0 w-full max-w-none flex-1 flex-col px-4 pt-5 sm:px-6 sm:pt-6 lg:px-8">
          <header className="flex shrink-0 items-center justify-between gap-4 border-b border-neutral-300 pb-5">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">
                Ajustes
              </h1>

              <p className="mt-1 text-sm text-neutral-500 sm:text-base">
                Configura este terminal para trabajar cómodamente.
              </p>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={requestLogout}
              aria-label="Cerrar sesión"
              className="h-12 shrink-0 gap-2 px-3 text-red-600 hover:bg-red-50 hover:text-red-700 sm:px-4"
            >
              <Icon icon="mdi:logout" className="text-xl" />

              <span className="hidden sm:inline">Cerrar sesión</span>
            </Button>
          </header>

          <div className="mt-6 grid min-h-0 min-w-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-6 lg:grid-cols-[20rem_minmax(0,1fr)] lg:gap-8 lg:grid-rows-1">
            <aside className="h-full min-h-0 min-w-0 self-start lg:sticky lg:top-4">
              <nav
                aria-label="Secciones de ajustes"
                className="flex snap-x snap-mandatory gap-2 overflow-x-auto pb-2 lg:flex-col lg:gap-1 lg:overflow-visible lg:pb-0 lg:pr-4"
              >
                {settingsSections.map((section) => {
                  const active = activeSection === section.key;

                  return (
                    <button
                      type="button"
                      key={section.key}
                      aria-current={active ? "page" : undefined}
                      onClick={() => setActiveSection(section.key)}
                      className={`flex min-h-16 min-w-56 shrink-0 snap-start touch-manipulation items-center gap-3 rounded-xl p-3 pr-4 text-left outline-none transition-colors active:scale-[0.99] focus-visible:ring-2 focus-visible:ring-blue-500/30 lg:min-w-0 ${
                        active
                          ? "bg-white text-blue-700 shadow-sm ring-1 ring-neutral-200"
                          : "text-neutral-600 hover:bg-neutral-200/40 hover:text-neutral-900"
                      }`}
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors ${
                          active
                            ? "bg-blue-50 text-blue-600"
                            : "bg-white text-neutral-400 ring-1 ring-neutral-200"
                        }`}
                      >
                        <Icon icon={section.icon} className="text-lg" />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold">
                          {section.label}
                        </span>

                        <span
                          className={`mt-0.5 block truncate text-xs ${
                            active ? "text-blue-600/70" : "text-neutral-500"
                          }`}
                        >
                          {section.description}
                        </span>
                      </span>

                      <Icon
                        icon="mdi:chevron-right"
                        className={`hidden shrink-0 text-xl lg:block ${
                          active ? "text-blue-500" : "text-neutral-300"
                        }`}
                      />
                    </button>
                  );
                })}
              </nav>
            </aside>

            <main
              data-bottom-nav-scroll
              className="min-h-0 min-w-0 overflow-y-auto pr-1 animate-fade animate-once"
            >
              <div className="pb-[calc(2.5rem_+_var(--bottom-nav-reserve))]">
                {activeSection === "general" && <SettingsGeneral />}

                {activeSection === "printers" && <SettingsPrinters />}

                {activeSection === "comandas" && <SettingsComandas />}

                {activeSection === "screensaver" && <SettingsScreensaver />}

                {activeSection === "security" && <SettingsSecurity />}
              </div>
            </main>
          </div>
        </div>
      </div>
    </PinGuard>
  );
}

function SettingsCard({
  icon,
  title,
  description,
  action,
  children,
  className = "",
}) {
  return (
    <section className={`border-b border-neutral-300 pb-7 ${className}`}>
      <header className="mb-5 flex items-start gap-4">
        {icon && (
          <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-neutral-500 shadow-sm ring-1 ring-neutral-200">
            <Icon icon={icon} className="text-xl" />
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h2 className="text-lg font-semibold text-neutral-900">{title}</h2>

            {action}
          </div>

          {description && (
            <p className="mt-1 text-sm leading-5 text-neutral-500">
              {description}
            </p>
          )}
        </div>
      </header>

      {children}
    </section>
  );
}

function SettingsRow({ icon, title, description, children, className = "" }) {
  return (
    <div
      className={`grid min-h-20 grid-cols-1 gap-4 border-b border-neutral-200 py-4 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_minmax(13rem,auto)] sm:items-center sm:gap-8 ${className}`}
    >
      <div className="flex min-w-0 items-start gap-3">
        {icon && (
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-neutral-400 shadow-sm ring-1 ring-neutral-200">
            <Icon icon={icon} className="text-lg" />
          </span>
        )}
        <div className="min-w-0">
          <div className="font-semibold text-neutral-800">{title}</div>

          {description && (
            <div className="mt-0.5 text-sm leading-5 text-neutral-500">
              {description}
            </div>
          )}
        </div>
      </div>
      <div className="w-full shrink-0 sm:w-auto sm:min-w-[13rem] sm:justify-self-end">
        {children}
      </div>
    </div>
  );
}

function SettingSwitch({ checked, onCheckedChange, label, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className="flex h-12 w-16 touch-manipulation items-center justify-center rounded-xl outline-none transition-colors hover:bg-neutral-200/50 focus-visible:ring-2 focus-visible:ring-blue-500/30 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span
        className={`relative h-7 w-12 rounded-full p-0.5 transition-colors ${
          checked ? "bg-blue-600" : "bg-neutral-300"
        }`}
      >
        <span
          className={`block h-6 w-6 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </span>
    </button>
  );
}

function SettingsGeneral() {
  const confirm = useConfirmDialog();
  const [updating, setUpdating] = useState(false);

  const developmentMode = localStorage.getItem("dev") === "true";

  function requestDevelopmentMode(checked) {
    confirm({
      title: "Cambiar el entorno de la API",
      description: checked
        ? "La aplicación se conectará al entorno de desarrollo y se reiniciará."
        : "La aplicación se conectará al entorno de producción y se reiniciará.",
      okText: "Cambiar",
      cancelText: "Cancelar",
      onOk: () => {
        localStorage.setItem("dev", checked);
        window.location.reload();
      },
    });
  }

  // La app de escritorio se queda con la versión cacheada en Chromium, así que
  // recargar no basta: hay que vaciar lo accesible y pedir la URL de nuevo.
  function requestUpdate() {
    confirm({
      title: "¿Actualizar la aplicación?",
      description:
        "Se borra la caché del programa y se recarga. Si estabas a mitad de una venta o de una comanda, se perderá.",
      okText: "Actualizar",
      cancelText: "Cancelar",
      onOk: async () => {
        setUpdating(true);
        try {
          await hardReload();
        } catch {
          setUpdating(false);
          toast.error("No se ha podido actualizar la aplicación");
        }
      },
    });
  }

  return (
    <div className="space-y-8">
      <div className="grid gap-x-10 gap-y-8 2xl:grid-cols-2">
        <SettingsCard
          icon="mdi:calendar-clock-outline"
          title="Operación"
          description="El periodo con el que se están creando las ventas."
        >
          <Exercise />
        </SettingsCard>

        <SettingsCard
          icon="mdi:tablet-dashboard"
          title="Dispositivo TPV"
          description="Vincula este terminal con el dispositivo de tu tienda."
        >
          <DeviceLink />
        </SettingsCard>
      </div>

      <SettingsCard
        icon="mdi:application-cog-outline"
        title="Aplicación"
        description="Opciones técnicas y acceso a las actualizaciones."
        className="border-b-0 pb-0"
      >
        <SettingsRow
          icon="mdi:flask-outline"
          title="Modo desarrollo"
          description="Usa la API de desarrollo en lugar de producción."
        >
          <SettingSwitch
            checked={developmentMode}
            onCheckedChange={requestDevelopmentMode}
            label="Modo desarrollo"
          />
        </SettingsRow>

        <SettingsRow
          icon="mdi:refresh"
          title="Reiniciar y actualizar"
          description="Recarga la aplicación y, si se ve anticuada, borra su caché."
        >
          <div className="flex w-full gap-2 sm:w-auto">
            <Button
              type="button"
              variant="outline"
              onClick={() => window.location.reload()}
              className="h-12 flex-1 px-4 sm:flex-none"
            >
              <Icon icon="mdi:reload" className="mr-2 text-xl" />
              Reiniciar
            </Button>

            <Button
              type="button"
              loading={updating}
              onClick={requestUpdate}
              aria-label="Borrar la caché y actualizar la aplicación"
              className="h-12 flex-1 px-4 sm:flex-none"
            >
              <Icon icon="mdi:cloud-sync-outline" className="mr-2 text-xl" />
              Actualizar
            </Button>
          </div>
        </SettingsRow>

        <SettingsRow
          icon="mdi:monitor-cellphone"
          title="Aplicación de escritorio"
          description="Descarga la última versión de Asoft TPV para este equipo."
        >
          <DesktopApp />
        </SettingsRow>

        <SettingsRow
          icon="mdi:update"
          title="Versión instalada"
          description="Consulta las novedades de la aplicación."
        >
          <ChangeLog />
        </SettingsRow>

        <SettingsRow
          icon="mdi:bug"
          title="Log del proceso principal"
          description="Ver en tiempo real lo que ocurre en Electron (impresión, formularios, errores)."
        >
          <MainProcessLog />
        </SettingsRow>

        
      </SettingsCard>
    </div>
  );
}

function SettingsScreensaver() {
  const [initialSettings] = useState(() => getScreensaverSettings());
  const [enabled, setEnabled] = useState(initialSettings.enabled);
  const [intervalMinutes, setIntervalMinutes] = useState(
    String(initialSettings.intervalMinutes),
  );
  const [backgroundColor, setBackgroundColor] = useState(
    initialSettings.backgroundColor,
  );
  const [imageUrl, setImageUrl] = useState(initialSettings.imageUrl);
  const [message, setMessage] = useState(initialSettings.message);
  const [showClock, setShowClock] = useState(initialSettings.showClock);
  const [saving, setSaving] = useState(false);

  function saveSettings(event) {
    event.preventDefault();
    setSaving(true);
    saveScreensaverSettings({
      enabled,
      intervalMinutes: Number(intervalMinutes),
      backgroundColor,
      imageUrl: imageUrl.trim(),
      message: message.trim(),
      showClock,
    });
    setSaving(false);
    toast.success("Ajustes del salvapantallas guardados");
  }

  function previewScreensaver() {
    requestScreensaverPreview({
      enabled,
      intervalMinutes: Number(intervalMinutes),
      backgroundColor,
      imageUrl: imageUrl.trim(),
      message: message.trim(),
      showClock,
    });
  }

  return (
    <div className="space-y-6">
      <SettingsCard
        icon="mdi:monitor-sleep"
        title="Salvapantallas"
        description="Se activa automáticamente cuando el TPV no recibe actividad."
      >
        <form onSubmit={saveSettings}>
          <SettingsRow
            icon="mdi:monitor-sleep"
            title="Activar salvapantallas"
            description="Se mostrará después del tiempo de inactividad configurado."
          >
            <SettingSwitch
              checked={enabled}
              onCheckedChange={setEnabled}
              label="Activar salvapantallas"
            />
          </SettingsRow>

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="screensaver-interval" className="text-sm font-semibold text-neutral-700">
                Tiempo de inactividad
              </Label>
              <Select value={intervalMinutes} onValueChange={setIntervalMinutes}>
                <SelectTrigger id="screensaver-interval" className="h-14 w-full text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1" className="min-h-11 py-2">1 minuto</SelectItem>
                  <SelectItem value="5" className="min-h-11 py-2">5 minutos</SelectItem>
                  <SelectItem value="10" className="min-h-11 py-2">10 minutos</SelectItem>
                  <SelectItem value="15" className="min-h-11 py-2">15 minutos</SelectItem>
                  <SelectItem value="30" className="min-h-11 py-2">30 minutos</SelectItem>
                  <SelectItem value="60" className="min-h-11 py-2">1 hora</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="screensaver-color" className="text-sm font-semibold text-neutral-700">
                Color de fondo
              </Label>
              <div className="flex gap-2">
                <Input
                  id="screensaver-color"
                  type="color"
                  value={backgroundColor}
                  onChange={(event) => setBackgroundColor(event.target.value)}
                  className="h-14 w-14 shrink-0 cursor-pointer p-1"
                />
                <Input
                  value={backgroundColor}
                  onChange={(event) => setBackgroundColor(event.target.value)}
                  aria-label="Código de color del salvapantallas"
                  className="h-14 flex-1 text-base uppercase"
                />
              </div>
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="screensaver-image" className="text-sm font-semibold text-neutral-700">
                Imagen de fondo opcional
              </Label>
              <Input
                id="screensaver-image"
                type="url"
                value={imageUrl}
                onChange={(event) => setImageUrl(event.target.value)}
                placeholder="https://..."
                className="h-14 text-base"
              />
              <p className="text-xs text-neutral-500">
                Usa una imagen HTTP(S). Si no se indica, se mostrará el color configurado.
              </p>
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="screensaver-message" className="text-sm font-semibold text-neutral-700">
                Mensaje
              </Label>
              <Input
                id="screensaver-message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Toca la pantalla para volver"
                maxLength={120}
                className="h-14 text-base"
              />
            </div>
          </div>

          <div className="mt-5 border-t border-neutral-200 pt-5">
            <SettingsRow
              icon="mdi:clock-outline"
              title="Mostrar reloj y fecha"
              description="Muestra la hora actual en el salvapantallas."
            >
              <SettingSwitch
                checked={showClock}
                onCheckedChange={setShowClock}
                label="Mostrar reloj y fecha"
              />
            </SettingsRow>
          </div>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <Button
              type="submit"
              loading={saving}
              className="h-14 px-5 text-base"
            >
              {!saving && <Icon icon="mdi:content-save-outline" className="mr-2 text-xl" />}
              Guardar cambios
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={previewScreensaver}
              className="h-14 px-5 text-base"
            >
              <Icon icon="mdi:monitor-sleep" className="mr-2 text-xl" />
              Probar ahora
            </Button>
          </div>
        </form>
      </SettingsCard>

      <div className="flex items-start gap-3 text-sm text-neutral-500">
        <Icon
          icon="mdi:gesture-tap"
          className="mt-0.5 shrink-0 text-lg text-neutral-400"
        />
        <p>
          El salvapantallas se cierra con el primer toque, clic o pulsación de
          tecla. El valor predeterminado es 5 minutos.
        </p>
      </div>
    </div>
  );
}

function SettingsSecurity() {
  const confirm = useConfirmDialog();
  const [configured, setConfigured] = useState(() => getPinConfig().configured);
  const [wizardOpen, setWizardOpen] = useState(false);

  function requestRemovePin() {
    confirm({
      title: "¿Eliminar el PIN?",
      description:
        "Tickets y Ajustes volverán a quedar accesibles sin contraseña en este TPV.",
      okText: "Eliminar PIN",
      cancelText: "Cancelar",
      variant: "danger",
      onOk: () => {
        clearPin();
        setConfigured(false);
        toast.success("PIN eliminado");
      },
    });
  }

  return (
    <>
      <SettingsCard
        icon="mdi:shield-lock-outline"
        title="PIN de acceso"
        description="Protege Tickets y Ajustes en cada entrada al TPV."
        action={
          <Badge variant={configured ? "success" : "secondary"}>
            {configured ? "Configurado" : "Sin configurar"}
          </Badge>
        }
      >
        <div
          className={`flex items-start gap-3 rounded-xl border p-4 ${
            configured
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-blue-200 bg-blue-50 text-blue-900"
          }`}
        >
          <Icon
            icon={
              configured ? "mdi:shield-check-outline" : "mdi:information-outline"
            }
            className="mt-0.5 shrink-0 text-xl"
          />

          <div>
            <div className="font-semibold">
              {configured ? "Protección activada" : "Protección desactivada"}
            </div>

            <p
              className={`mt-1 text-sm ${configured ? "text-emerald-800" : "text-blue-800"}`}
            >
              {configured
                ? "Se solicitará el PIN cada vez que entres en Tickets o Ajustes."
                : "Configura un PIN de 4 a 6 dígitos para activar la protección."}
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-4 border-t border-neutral-200 pt-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div>
            <div className="font-semibold text-neutral-800">
              {configured ? "Cambiar el PIN de acceso" : "Activar el PIN de acceso"}
            </div>
            <p className="mt-1 text-sm text-neutral-500">
              Se usará el mismo teclado táctil que al desbloquear el TPV.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              onClick={() => setWizardOpen(true)}
              className="h-14 px-5 text-base"
            >
              <Icon icon="mdi:shield-key-outline" className="mr-2 text-xl" />
              {configured ? "Cambiar PIN" : "Establecer PIN"}
            </Button>
            {configured && (
              <Button
                type="button"
                variant="outline"
                onClick={requestRemovePin}
                className="h-14 border-red-200 px-5 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Icon icon="mdi:shield-off-outline" className="mr-2 text-xl" />
                Eliminar PIN
              </Button>
            )}
          </div>
        </div>
      </SettingsCard>

      <PinSetupWizard
        open={wizardOpen}
        setOpen={setWizardOpen}
        configured={configured}
        onSaved={() => {
          setConfigured(true);
          toast.success("PIN de acceso actualizado");
        }}
      />
    </>
  );
}

function Exercise() {
  const confirm = useConfirmDialog();
  const currentExerciseId = String(localStorage.getItem("exercise_id") || "");

  const { data, isLoading } = useQuery({
    queryKey: ["exercises", localStorage.getItem("shop_id")],
    queryFn: () =>
      Get({
        request: `/exercises?shop_id=${localStorage.getItem("shop_id")}`,
      }),
  });

  if (isLoading) {
    return (
      <div
        className="h-20 animate-pulse rounded-xl bg-neutral-100"
        aria-label="Cargando ejercicios"
      />
    );
  }

  function onChangeExercise(exercise) {
    confirm({
      title: "Cambiar de ejercicio",
      description:
        "La aplicación se reiniciará para abrir el ejercicio seleccionado.",
      okText: "Cambiar",
      cancelText: "Cancelar",
      variant: "danger",
      onOk: () => {
        localStorage.setItem("exercise_id", exercise);
        window.location.reload();
      },
    });
  }

  return (
    <SettingsRow
      icon="mdi:calendar-range"
      title="Ejercicio fiscal"
      description="Selecciona el periodo que debe estar activo en este TPV."
    >
      <Select value={currentExerciseId} onValueChange={onChangeExercise}>
        <SelectTrigger className="h-14 w-full text-base sm:w-80">
          <SelectValue placeholder="Selecciona un ejercicio" />
        </SelectTrigger>
        <SelectContent>
          {(data || []).map((exercise) => (
            <SelectItem
              key={exercise.id}
              value={String(exercise.id)}
              className="min-h-11 py-2"
            >
              {exercise.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </SettingsRow>
  );
}

function DeviceLink() {
  const confirm = useConfirmDialog();
  const [token, setToken] = useState("");
  const [device, setDevice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [loadError, setLoadError] = useState("");

  const currentShopId = localStorage.getItem("shop_id");
  const storedToken = localStorage.getItem(DEVICE_TOKEN_KEY);
  const storedShopId = localStorage.getItem(DEVICE_SHOP_ID_KEY);
  const linkedToOtherShop = Boolean(
    storedToken && storedShopId && storedShopId !== currentShopId,
  );

  useEffect(() => {
    let cancelled = false;

    if (!storedToken || storedShopId !== currentShopId) {
      setDevice(null);
      setChecking(false);
      return () => {
        cancelled = true;
      };
    }

    setChecking(true);
    heartbeatDevice(storedToken)
      .then((result) => {
        if (cancelled) return;
        if (result.device) {
          setDevice(result.device);

          setLoadError("");
        } else {
          setDevice(null);

          setLoadError(
            result.error || "No se ha podido leer el dispositivo vinculado",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentShopId, storedShopId, storedToken]);

  async function linkDevice(event) {
    event.preventDefault();
    const candidateToken = token.trim();

    if (saving) return;
    if (!candidateToken) {
      toast.error("Pega el token del dispositivo");
      return;
    }

    setSaving(true);
    const result = await heartbeatDevice(candidateToken);
    setSaving(false);

    if (result.error || !result.device) {
      toast.error(
        result.error || "El token no corresponde a un dispositivo válido",
      );
      return;
    }

    if (String(result.device.shop_id) !== String(currentShopId)) {
      toast.error("Ese token pertenece a otra tienda");
      return;
    }

    localStorage.setItem(DEVICE_TOKEN_KEY, candidateToken);
    localStorage.setItem(DEVICE_SHOP_ID_KEY, String(result.device.shop_id));
    setDevice(result.device);
    setToken("");
    setLoadError("");
    notifyDeviceChanged();
    toast.success("TPV vinculado correctamente");
  }

  function unlinkDevice() {
    confirm({
      title: "¿Desvincular este TPV?",
      description:
        "Dejará de comunicarse con el servidor como dispositivo hasta que vuelvas a vincularlo.",
      okText: "Desvincular",
      cancelText: "Cancelar",
      variant: "danger",
      onOk: () => {
        localStorage.removeItem(DEVICE_TOKEN_KEY);
        localStorage.removeItem(DEVICE_SHOP_ID_KEY);
        setDevice(null);
        setLoadError("");
        notifyDeviceChanged();
        toast.success("TPV desvinculado");
      },
    });
  }

  if (device) {
    return (
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-xl text-blue-600 ring-1 ring-blue-100">
            <Icon icon={device.icon || "mdi:cash-register"} />
          </span>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="truncate text-lg font-semibold text-neutral-900">
                {device.nombre}
              </div>

              <Badge variant={device.online ? "success" : "warning"}>
                {checking
                  ? "Comprobando"
                  : device.online
                    ? "Conectado"
                    : "Sin conexión"}
              </Badge>
            </div>

            <div className="mt-1 truncate text-sm text-neutral-500">
              {device.location || "Ubicación sin asignar"}
            </div>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={unlinkDevice}
          className="h-12 w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 sm:w-auto sm:px-5"
        >
          <Icon icon="mdi:link-variant-off" className="mr-2 text-xl" />
          Desvincular
        </Button>
      </div>
    );
  }

  return (
    <div>
      <form className="flex flex-col gap-3 sm:flex-row" onSubmit={linkDevice}>
        <div className="min-w-0 flex-1">
          <label htmlFor="device-token" className="sr-only">
            Token del dispositivo
          </label>

          <Input
            id="device-token"
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={token}
            onChange={(event) => setToken(event.target.value)}
            placeholder="Pega aquí el token del dispositivo"
            className="h-14 text-base"
          />
        </div>
        <Button
          type="submit"
          loading={saving}
          className="h-14 shrink-0 px-5 text-base"
        >
          {!saving && <Icon icon="mdi:link-variant" className="mr-2 text-xl" />}
          Vincular TPV
        </Button>
      </form>

      {linkedToOtherShop && (
        <div className="mt-3 flex items-start gap-2 border-y border-amber-200 p-3 text-sm text-amber-800">
          <Icon icon="mdi:alert-outline" className="mt-0.5 shrink-0 text-lg" />
          Este terminal conserva un dispositivo de otra tienda y no está
          enviando su conexión.
        </div>
      )}
      {checking && (
        <p className="mt-3 text-sm text-neutral-500">
          Comprobando el dispositivo vinculado…
        </p>
      )}
      {loadError && (
        <p className="mt-3 flex items-center gap-2 text-sm text-red-600">
          <Icon icon="mdi:alert-circle-outline" className="text-lg" />

          {loadError}
        </p>
      )}
    </div>
  );
}

function DesktopApp() {
  const installed = installedVersion();
  const showAlert = useAlertDialog();

  const { data, error, isLoading, isError, refetch } = useQuery({
    queryKey: ["desktop-release"],
    queryFn: fetchLatestRelease,
    enabled: isDesktopApp(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  if (!isDesktopApp()) {
    return (
      <div className="flex items-center gap-3">
        <Badge variant="warning">Solo en escritorio</Badge>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="h-14 w-full animate-pulse rounded-xl bg-neutral-100 sm:w-56"
        aria-label="Comprobando versiones"
      />
    );
  }

  if (isError || !data?.installerUrl) {
    const noReleases = error?.code === "no-releases";

    return (
      <div className="flex items-center gap-3">
        <Badge variant="warning">
          {noReleases ? "Sin versiones" : "No se ha comprobado"}
        </Badge>

        {!noReleases && (
          <Button
            type="button"
            variant="outline"
            onClick={() => refetch()}
            className="h-12 shrink-0 px-4"
          >
            <Icon icon="mdi:refresh" className="mr-2 text-xl" />
            Reintentar
          </Button>
        )}
      </div>
    );
  }

  const pending = isNewerVersion(data.tag, installed);

  return (
    <div className="flex w-full flex-col gap-3 sm:w-auto sm:items-end">
      <div className="flex flex-wrap items-center gap-2">
        {pending ? (
          <Badge variant="success">Nueva versión</Badge>
        ) : (
          <Badge>Actualizada</Badge>
        )}

        <span className="text-sm text-neutral-500">
          {installed ? `Instalada ${installed} · ` : ""}
          Última {data.tag.replace(/^v/i, "")}
        </span>
      </div>

      <div className="flex w-full gap-2 sm:w-auto">
        <Button
          type="button"
          onClick={() =>
            showAlert({
              title: `Novedades de la ${data.tag}`,
              content: data.notes ? (
                <Markdown>{data.notes}</Markdown>
              ) : (
                "No hay detalles disponibles para esta versión."
              ),
            })
          }
          disabled={!data.notes}
          className="h-12 flex-1 px-4 sm:flex-none"
        >
          <Icon icon="mdi:history" className="mr-2 text-xl" />
          Novedades
        </Button>

        {/* Enlace de verdad: en la app de escritorio Chromium lo descarga en la
            carpeta de descargas en vez de navegar fuera del TPV. */}
        <Button asChild className="h-12 flex-1 px-4 sm:flex-none">
          <a
            href={data.installerUrl}
            target="_blank"
            rel="noreferrer noopener"
          >
            <Icon icon="mdi:download" className="mr-2 text-xl" />
            Descargar
          </a>
        </Button>
      </div>

      <span className="text-xs text-neutral-400">
        {data.installerName}
        {formatSize(data.installerSize)
          ? ` · ${formatSize(data.installerSize)}`
          : ""}
      </span>
    </div>
  );
}

function ChangeLog() {
  const [version, setVersion] = useState(null);
  const [versionData, setVersionData] = useState(null);
  const currentVersion = useRef(null);
  const showAlert = useAlertDialog();

  useEffect(() => {
    let active = true;

    async function getVersion() {
      try {
        const response = await fetch(
          "https://git.asoft.es/api/v1/repos/ASoft/tpv-frontend/releases/latest?token=6230120f17b27a0f44af3d50718af711996ce8cc",
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!active) return;

        setVersionData(data);
        if (
          currentVersion.current &&
          currentVersion.current !== data.tag_name
        ) {
          window.location.reload();

          return;
        }
        currentVersion.current = data.tag_name;
        setVersion(data.tag_name);
      } catch {
        // La aplicación debe seguir funcionando si no se puede comprobar.
      }
    }

    getVersion();
    const interval = window.setInterval(getVersion, 30000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, []);

  function showModal() {
    if (!versionData) return;
    showAlert({
      title: versionData.name || "Novedades de la aplicación",
      content: versionData.body ? (
        <Markdown>{versionData.body}</Markdown>
      ) : (
        "No hay detalles disponibles para esta versión."
      ),
    });
  }

  return (
    <Button
      type="button"
      variant="outline"
      disabled={!versionData}
      onClick={showModal}
      className="h-12 w-full px-4 sm:w-auto"
    >
      <Icon icon="mdi:history" className="mr-2 text-xl" />
      {version ? `Versión ${version}` : "Comprobando versión"}
    </Button>
  );
}



function SettingsComandas() {
  const { user } = useContext(UserContext);
  const confirm = useConfirmDialog();
  const hasComandasPack = hasFeature(user, "hosteleria_comandas");
  // La impresión directa está activa mientras no se desactive a mano, que es lo
// que hace getComandaOptions() (`!= 'false'`): el interruptor tiene que leer
// lo mismo, o parece apagado cuando en realidad las comandas van solas al
// papel. Con `=== "true"` salía apagado de fábrica.
const silentComandas = localStorage.getItem("comandsilentPrint") !== "false";
  const [comandaSize, setComandaSize] = useState(
    localStorage.getItem("comandticketSize") || "80mm",
  );

  function requestRestartToggle(checked) {
    confirm({
      title: "Cambiar la impresión de comandas",
      description:
        "La aplicación se reiniciará para aplicar el nuevo modo de impresión.",
      okText: "Cambiar",
      cancelText: "Cancelar",
      variant: "danger",
      onOk: () => {
        localStorage.setItem("comandsilentPrint", checked);
        window.location.reload();
      },
    });
  }

  function updateComandaSize(size) {
    localStorage.setItem("comandticketSize", size);
    setComandaSize(size);
    toast.success("Tamaño de papel guardado");
  }

  return (
    <div className="grid gap-8 2xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
      <SettingsCard
        icon="mdi:qrcode"
        title="Dispositivo Comandas"
        description="Vincula el dispositivo que recibe las comandas de cocina."
      >
        <QrDevice />
      </SettingsCard>

      {hasComandasPack && (
        <SettingsCard
          icon="mdi:printer-outline"
          title="Impresión de comandas"
          description="Selecciona la impresora y el papel del pack Comandas."
          action={<Badge variant="success">Pack Comandas</Badge>}
          className="border-b-0 pb-0"
        >
          <Printers storage="comandprinter" showTest printOptions={getComandaOptions} />

          <SettingsRow
            icon="mdi:printer-pos"
            title="Tamaño del papel"
            description="Tamaño utilizado por las comandas de cocina."
          >
            <Select value={comandaSize} onValueChange={updateComandaSize}>
              <SelectTrigger className="h-14 w-full text-base sm:w-56">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="58mm" className="min-h-11 py-2">
                  58 mm
                </SelectItem>

                <SelectItem value="80mm" className="min-h-11 py-2">
                  80 mm
                </SelectItem>
              </SelectContent>
            </Select>
          </SettingsRow>

          <SettingsRow
            icon="mdi:printer-check"
            title="Impresión directa"
            description="Envía las comandas directamente a su impresora."
          >
            <SettingSwitch
              checked={silentComandas}
              onCheckedChange={requestRestartToggle}
              label="Impresión directa de comandas"
            />
          </SettingsRow>
        </SettingsCard>
      )}
    </div>
  );
}

function SettingsPrinters() {
  const { shop } = useContext(UserContext);
  const confirm = useConfirmDialog();
  const queryClient = useQueryClient();

  const [paperSaving, setPaperSaving] = useState(
    shop?.ticket_settings?.paper_saving || false,
  );
  const [savingPaperSetting, setSavingPaperSetting] = useState(false);
  const [ticketSize, setTicketSize] = useState(
    localStorage.getItem("ticketSize") || "80mm",
  );
  const silentPrint = localStorage.getItem("silentPrint") === "true";
  const previewPrint = localStorage.getItem("previewPrint") === "true";

  function requestRestartToggle({ checked, storageKey, title, description }) {
    confirm({
      title,
      description,
      okText: "Cambiar",
      cancelText: "Cancelar",
      variant: "danger",
      onOk: () => {
        localStorage.setItem(storageKey, checked);
        window.location.reload();
      },
    });
  }

  function updatePaperSize(size) {
    localStorage.setItem("ticketSize", size);
    setTicketSize(size);
    toast.success("Tamaño de papel guardado");
  }

  async function togglePaperSaving(checked) {
    setSavingPaperSetting(true);
    const newSettings = {
      ...shop?.ticket_settings,
      paper_saving: checked,
    };

    try {
      const response = await Put({
        request: `/shops/${localStorage.getItem("shop_id")}?shop_id=${localStorage.getItem("shop_id")}`,
        data: { ticket_settings: JSON.stringify(newSettings) },
      });
      if (response?.error) throw new Error(response.error);

      setPaperSaving(checked);
      queryClient.setQueryData(["shop", localStorage.getItem("shop_id")], {
        ...shop,
        ticket_settings: newSettings,
      });
      toast.success("Ajuste guardado");
    } catch (error) {
      toast.error(error.message || "No se ha podido guardar el ajuste");
    } finally {
      setSavingPaperSetting(false);
    }
  }

  return (
    <div className="space-y-6">
      <SettingsCard
        icon="mdi:receipt-text-outline"
        title="Tickets"
        description="Impresora, papel y comportamiento de la venta."
        action={<Badge variant="blue">Principal</Badge>}
        className="border-b-0 pb-0"
      >
        <Printers storage="printer" showTest printOptions={getTicketOptions} />

        <SettingsRow
          icon="mdi:printer-pos"
          title="Tamaño del papel"
          description="Debe coincidir con el rollo instalado en la impresora."
        >
          <Select value={ticketSize} onValueChange={updatePaperSize}>
            <SelectTrigger className="h-14 w-full text-base sm:w-56">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="58mm" className="min-h-11 py-2">
                58 mm
              </SelectItem>

              <SelectItem value="80mm" className="min-h-11 py-2">
                80 mm
              </SelectItem>
            </SelectContent>
          </Select>
        </SettingsRow>

        <SettingsRow
          icon="mdi:printer-check"
          title="Impresión directa"
          description="Envía el ticket a la impresora sin mostrar una vista previa."
        >
          <SettingSwitch
            checked={silentPrint}
            onCheckedChange={(checked) =>
              requestRestartToggle({
                checked,

                storageKey: "silentPrint",

                title: "Cambiar la impresión directa",

                description:
                  "La aplicación se reiniciará para aplicar el nuevo modo de impresión.",
              })
            }
            label="Impresión directa"
          />
        </SettingsRow>

        <SettingsRow
          icon="mdi:monitor-eye"
          title="Vista previa"
          description="Abre el ticket en una ventana antes de imprimirlo."
        >
          <SettingSwitch
            checked={previewPrint}
            onCheckedChange={(checked) =>
              requestRestartToggle({
                checked,

                storageKey: "previewPrint",

                title: "Cambiar la vista previa",

                description:
                  "La aplicación se reiniciará para aplicar el nuevo comportamiento.",
              })
            }
            label="Vista previa"
          />
        </SettingsRow>

        <SettingsRow
          icon="mdi:leaf"
          title="Ahorro de papel"
          description="Reduce márgenes y elementos gráficos para gastar menos papel."
        >
          <SettingSwitch
            checked={paperSaving}
            onCheckedChange={togglePaperSaving}
            disabled={savingPaperSetting}
            label="Ahorro de papel"
          />
        </SettingsRow>
      </SettingsCard>
    </div>
  );
}

function Printers({ storage = "printer", showTest = false, printOptions }) {
  const confirm = useConfirmDialog();
  const { shop } = useContext(UserContext);
  const selectedPrinter = localStorage.getItem(storage) || "";
  const electronAvailable = typeof window.electron?.getPrinters === "function";

  const { data, isLoading } = useQuery({
    queryKey: ["printers"],
    queryFn: () => window.electron.getPrinters(),
    enabled: electronAvailable,
  });

  function onSelectPrinter(printer) {
    confirm({
      title: "Cambiar de impresora",
      description:
        "La aplicación se reiniciará para aplicar la nueva impresora.",
      okText: "Cambiar",
      cancelText: "Cancelar",
      variant: "danger",
      onOk: () => {
        localStorage.setItem(storage, printer);
        window.location.reload();
      },
    });
  }

  function testPrint() {
    // La prueba sale por la impresora de esta sección, no por la de tickets:
    // si no, no sirve para calibrar el tamaño de las comandas.
    printTicket(
      GenerateTicketExample(shop?.ticket_settings?.paper_saving || false),
      printOptions ? printOptions() : undefined,
    );
  }

  if (isLoading) {
    return (
      <div
        className="h-20 animate-pulse rounded-xl bg-neutral-100"
        aria-label="Cargando impresoras"
      />
    );
  }

  if (!electronAvailable) {
    return (
      <SettingsRow
        icon="mdi:printer-alert"
        title="Impresora"
        description="La impresión directa solo está disponible en la aplicación de escritorio."
      >
        <Badge variant="warning">No disponible</Badge>
      </SettingsRow>
    );
  }

  if (!data?.length) {
    return (
      <SettingsRow
        icon="mdi:printer-alert"
        title="Impresora"
        description="No se han encontrado impresoras conectadas."
      >
        <Badge variant="warning">Sin impresoras</Badge>
      </SettingsRow>
    );
  }

  return (
    <SettingsRow
      icon="mdi:printer"
      title="Impresora"
      description="Selecciona la impresora utilizada para este tipo de documento."
    >
      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
        {showTest && (
          <Button
            type="button"
            variant="outline"
            onClick={testPrint}
            className="h-14 shrink-0 px-4"
          >
            <Icon icon="mdi:printer-pos" className="mr-2 text-xl" />
            Probar
          </Button>
        )}
        <Select value={selectedPrinter} onValueChange={onSelectPrinter}>
          <SelectTrigger className="h-14 w-full text-base sm:w-80">
            <SelectValue placeholder="Selecciona una impresora" />
          </SelectTrigger>

          <SelectContent>
            {data.map((printer) => (
              <SelectItem
                key={printer.name}
                value={printer.name}
                className="min-h-11 py-2"
              >
                {printer.displayName || printer.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </SettingsRow>
  );
}
